import { useState, useEffect, useCallback, useRef } from 'react';
import { CreditService, CreditBalance, CreditTransaction } from '@/services/creditService';
import { useAuth } from '@/hooks/useAuth';

export interface UseCreditsReturn {
  balance: number;
  plan: CreditBalance['plan'];
  isLoading: boolean;
  error: string | null;
  transactions: CreditTransaction[];
  refetch: () => Promise<void>;
  checkCredits: (actionType: string) => Promise<{ hasEnough: boolean; required: number; available: number }>;
  deductCredits: (actionType: string, metadata?: Record<string, unknown>) => Promise<{ success: boolean; error?: string; credits_used?: number; new_balance?: number }>;
  isLowBalance: boolean;
  isCriticalBalance: boolean;
}

export function useCredits(): UseCreditsReturn {
  const { user } = useAuth();
  // Supabase hands out a new user object each time the tab regains focus. Key on
  // the id: the object change used to refetch here and flip isLoading, and
  // PaywallGuard then tore down a running tailoring engine (Nov 4 2026).
  const userId = user?.id;
  const loadedFor = useRef<string | undefined>(undefined);
  const [balance, setBalance] = useState<number>(0);
  const [plan, setPlan] = useState<CreditBalance['plan']>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);

  const fetchBalance = useCallback(async () => {
    if (!userId) {
      setBalance(0);
      setPlan(null);
      setIsLoading(false);
      return;
    }

    try {
      if (loadedFor.current !== userId) setIsLoading(true);
      setError(null);
      const data = await CreditService.checkBalance();
      setBalance(data.balance);
      setPlan(data.plan);
      loadedFor.current = userId;
    } catch (err) {
      console.error('Error fetching credit balance:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch balance');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const fetchTransactions = useCallback(async () => {
    if (!userId) return;
    
    try {
      const txs = await CreditService.getTransactions(20);
      setTransactions(txs);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  }, [userId]);

  const refetch = useCallback(async () => {
    await Promise.all([fetchBalance(), fetchTransactions()]);
  }, [fetchBalance, fetchTransactions]);

  const checkCredits = useCallback(async (actionType: string) => {
    return CreditService.hasEnoughCredits(actionType);
  }, []);

  const deductCredits = useCallback(async (
    actionType: string, 
    metadata?: Record<string, unknown>
  ): Promise<{ success: boolean; error?: string; credits_used?: number; new_balance?: number }> => {
    const result = await CreditService.deductCredits(actionType, metadata);
    
    if (result.success && result.new_balance !== undefined) {
      setBalance(result.new_balance);
      // Refresh transactions
      fetchTransactions();
    }
    
    return { 
      success: result.success, 
      error: result.error,
      credits_used: result.credits_used,
      new_balance: result.new_balance
    };
  }, [fetchTransactions]);

  useEffect(() => {
    fetchBalance();
    fetchTransactions();
  }, [fetchBalance, fetchTransactions]);

  // Calculate warning thresholds
  const isLowBalance = balance > 0 && balance <= 10;
  const isCriticalBalance = balance <= 3;

  return {
    balance,
    plan,
    isLoading,
    error,
    transactions,
    refetch,
    checkCredits,
    deductCredits,
    isLowBalance,
    isCriticalBalance
  };
}